import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { 
  FileText, 
  Brain, 
  Shield, 
  Zap, 
  Globe, 
  Clock, 
  Users, 
  CheckCircle,
  FileSearch,
  Download,
  DollarSign
} from 'lucide-react';

const Features = () => {
  const features = [
    {
      icon: FileText,
      title: "30+ Document Formats",
      description: "Convert contracts, invoices, and business documents between PDF, Word, and many more formats.",
      highlights: ["PDF, DOCX, TXT, RTF", "Batch processing", "Perfect formatting"],
      gradient: "from-blue-500 to-indigo-500",
      bgGradient: "from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20"
    },
    {
      icon: Brain,
      title: "AI Document Analysis",
      description: "Get plain-English explanations of complex contract terms and automatic risk detection.",
      highlights: ["Contract summaries", "Risk alerts", "Key terms highlighted"],
      gradient: "from-violet-500 to-purple-500",
      bgGradient: "from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20"
    },
    {
      icon: DollarSign,
      title: "Save on Legal Costs",
      description: "Handle routine legal documents yourself instead of paying expensive attorney hourly rates.",
      highlights: ["90% cost savings", "No hourly fees", "Instant results"],
      gradient: "from-green-500 to-emerald-500",
      bgGradient: "from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20"
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Process documents in seconds, not days. No waiting for appointments or callbacks.",
      highlights: ["< 3 second conversion", "Instant analysis", "24/7 availability"],
      gradient: "from-amber-500 to-orange-500",
      bgGradient: "from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20"
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "Your business documents are protected with bank-level encryption and never shared.",
      highlights: ["256-bit encryption", "Private processing", "Auto-deletion"],
      gradient: "from-slate-600 to-slate-800",
      bgGradient: "from-slate-50 to-gray-50 dark:from-slate-800/30 dark:to-gray-800/30"
    },
    {
      icon: Users,
      title: "Built for Business",
      description: "Designed for small business owners who need to handle contracts and agreements confidently.",
      highlights: ["Easy to use", "No legal jargon", "Business-focused"],
      gradient: "from-rose-500 to-pink-500",
      bgGradient: "from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20"
    }
  ];

  return (
    <section id="features" className="py-20 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Built for Small Business Owners Like You
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-3xl mx-auto">
            Stop overpaying for legal document services. Our tools give you the power to handle 
            contracts, agreements, and business documents on your own terms.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <Card 
                key={index} 
                className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 bg-gradient-to-br from-white to-slate-50/50 dark:from-slate-800 dark:to-slate-900 overflow-hidden"
              >
                <CardHeader className={`pb-6 bg-gradient-to-br ${feature.bgGradient} border-b border-opacity-20 dark:border-slate-700`}>
                  <div className={`p-4 bg-gradient-to-br ${feature.gradient} rounded-2xl w-fit mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-xl font-bold text-slate-900 dark:text-white group-hover:text-slate-800 dark:group-hover:text-slate-100 transition-colors">
                    {feature.title}
                  </CardTitle>
                  <CardDescription className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3">
                    {feature.highlights.map((highlight, highlightIndex) => (
                      <li key={highlightIndex} className="flex items-center text-sm">
                        <div className={`p-1 rounded-full bg-gradient-to-r ${feature.gradient} mr-3`}>
                          <CheckCircle className="h-3 w-3 text-white" />
                        </div>
                        <span className="text-slate-700 dark:text-slate-300 font-medium">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* How It Works Section */}
        <div className="mt-24">
          <div className="text-center mb-16">
            <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white mb-4">
              As Easy as 1-2-3
            </h3>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              No legal expertise required. Get professional results in seconds.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="group text-center">
              <div className="relative mb-8">
                <div className="p-6 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl w-fit mx-auto mb-6 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2">
                  <FileText className="h-12 w-12 text-white" />
                </div>
                <div className="absolute -top-3 -right-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-lg font-bold rounded-full w-12 h-12 flex items-center justify-center shadow-lg">
                  1
                </div>
              </div>
              <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl border-2 border-blue-200 dark:border-blue-800">
                <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Upload Your Document</h4>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                  Drop in any contract, agreement, invoice, or business document. 
                  We support PDF, Word, and 30+ other formats.
                </p>
              </div>
            </div>

            <div className="group text-center">
              <div className="relative mb-8">
                <div className="p-6 bg-gradient-to-br from-violet-500 to-purple-500 rounded-2xl w-fit mx-auto mb-6 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2">
                  <Brain className="h-12 w-12 text-white" />
                </div>
                <div className="absolute -top-3 -right-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-lg font-bold rounded-full w-12 h-12 flex items-center justify-center shadow-lg">
                  2
                </div>
              </div>
              <div className="p-6 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/30 dark:to-purple-900/30 rounded-2xl border-2 border-violet-200 dark:border-violet-800">
                <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Choose What You Need</h4>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                  Convert to a different format, get an AI-powered plain-English summary, 
                  or scan for potential risks and issues.
                </p>
              </div>
            </div>

            <div className="group text-center">
              <div className="relative mb-8">
                <div className="p-6 bg-gradient-to-br from-emerald-500 to-green-500 rounded-2xl w-fit mx-auto mb-6 shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-2">
                  <Download className="h-12 w-12 text-white" />
                </div>
                <div className="absolute -top-3 -right-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white text-lg font-bold rounded-full w-12 h-12 flex items-center justify-center shadow-lg">
                  3
                </div>
              </div>
              <div className="p-6 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 rounded-2xl border-2 border-emerald-200 dark:border-emerald-800">
                <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Get Results Instantly</h4>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                  Download your converted document or review your analysis. 
                  Save hundreds in legal fees and get results in seconds.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;